// Les emplacements de la coquille qu'un écran peut remplir : le sous-en-tête
// d'une vue de détail, les actions à droite du titre, le panneau latéral.
//
// Ces trois-là changent avec l'état de l'écran (un panneau s'ouvre, un
// sous-en-tête apparaît quand on entre dans un travail). Les faire transiter
// par un état du contexte ferait boucler le rendu : l'écran produirait un
// nouveau fragment à chaque tour. Ils passent donc par des portails, qui
// déposent le fragment dans le bon nœud sans rien faire remonter.

import type { ReactNode } from "react";
import { createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";

export type NomAncre = "sous-entete" | "actions-scope" | "panneau";

interface Ancres {
  noeuds: Partial<Record<NomAncre, HTMLElement | null>>;
  // Un écran qui pose son propre panneau prend la place des notifications :
  // la coquille a besoin de le savoir pour ne pas afficher les deux.
  occuper: (ancre: NomAncre) => void;
  liberer: (ancre: NomAncre) => void;
}

const AncresContexte = createContext<Ancres>({
  noeuds: {},
  occuper: () => {},
  liberer: () => {},
});

interface FournisseurAncresProps extends Ancres {
  children: ReactNode;
}

export function FournisseurAncres({
  noeuds,
  occuper,
  liberer,
  children,
}: FournisseurAncresProps) {
  // La valeur est memoïsée par l'appelant (le layout) : voir LayoutCompta.
  const valeur = { noeuds, occuper, liberer };
  return (
    <AncresContexte.Provider value={valeur}>{children}</AncresContexte.Provider>
  );
}

interface PortailCoquilleProps {
  ancre: NomAncre;
  children: ReactNode;
}

/**
 * Dépose son contenu dans l'emplacement nommé de la coquille. Tant que la
 * coquille n'est pas montée, il ne rend rien plutôt que de rendre au mauvais
 * endroit.
 */
export function PortailCoquille({ ancre, children }: PortailCoquilleProps) {
  const { noeuds, occuper, liberer } = useContext(AncresContexte);

  useEffect(() => {
    occuper(ancre);
    return () => liberer(ancre);
  }, [ancre, occuper, liberer]);

  const noeud = noeuds[ancre];
  if (!noeud) {
    return null;
  }
  return createPortal(children, noeud);
}
